import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { mapApiError } from "@njbsoft/i18n";
import { Button, EmptyState, PageHeader, PageSkeleton } from "@/components/ui";
import { AboutCard } from "@/components/notices/compose/about-card";
import { CcCard } from "@/components/notices/compose/cc-card";
import { EnclosuresCard } from "@/components/notices/compose/enclosures-card";
import { defaultEnclosureTicks } from "@/components/notices/enclosures/enclosure-selection";
import { RecipientResolveCard } from "@/components/notices/compose/recipient-resolve-card";
import {
  StartFromCard,
  type LetterSource,
  type NoticeLocale,
} from "@/components/notices/compose/start-from-card";
import { subjectFromSearch } from "@/components/notices/compose/about-subject";
import { boundInspectionFact } from "@/components/notices/compose/bound-inspection";
import { boundTestFact } from "@/components/notices/compose/bound-test";
import { ComposeFailureAlert } from "@/components/notices/compose/compose-failure-alert";
import {
  noticeComposeFailure,
  type NoticeComposeFailure,
} from "@/components/notices/compose/notice-create-error";
import { summaryAccount } from "@/components/notices/compose/summary-account";
import { useSubjectSummary } from "@/components/notices/compose/use-subject-summary";
import { caseAwaitsInstallation } from "@/components/change-requests/case-awaits-installation";
import { PendingInstallationWarning } from "@/components/change-requests/pending-installation-warning";
import { usePendingNewInstallation } from "@/components/change-requests/use-pending-new-installation";
import { useNoticeEnclosureCandidates } from "@/hooks/use-notice-enclosure-candidates";
import { useFormSubmission } from "@/hooks/use-form-submissions";
import { useOrgComplianceSettings } from "@/hooks/use-org-compliance-settings";
import { usePermissions } from "@/hooks/use-permissions";
import { useAppStore } from "@/store";
import { useViolation } from "@/hooks/use-violations";
import {
  noticeKeys,
  noticeSubjectParams,
  useCreateNotice,
  useResolveNoticeRecipient,
  type NoticeCcRecipient,
  type NoticeCreate,
  type NoticeRecipient,
  type NoticeSubjectRef,
} from "@/hooks/use-notices";

/** The starter family that notices a disconnection for a missing assembly. */
const PENDING_DISCONNECTION_KIND = "cc14_pending_disconnection_not_installed";

/** Identity of one picked subject, for the once-per-subject seeding of copies and enclosures. */
function subjectKey(ref: NoticeSubjectRef | null): string | null {
  return ref ? `${ref.kind}:${ref.id}` : null;
}

interface NewNoticeSearch {
  facility_id?: string;
  device_installation_id?: string;
  account_id?: string;
  /** The testing company a door on its detail page composes a letter about. */
  testing_company_id?: string;
  /** The enforcement case a door on the case file composes for. */
  violation_id?: string;
  /**
   * The accepted submission a door on the submission page reports on — an
   * inspection, or a device test whose door names the tested assembly.
   */
  submission_id?: string;
  /** The starter kind a door opens the composer for, picked once if the subject can fill it. */
  letter_kind?: string;
}

function readParam(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

export const Route = createFileRoute("/_authenticated/notices/new/")({
  component: NewNoticePage,
  validateSearch: (search: Record<string, unknown>): NewNoticeSearch => ({
    facility_id: readParam(search.facility_id),
    device_installation_id: readParam(search.device_installation_id),
    account_id: readParam(search.account_id),
    testing_company_id: readParam(search.testing_company_id),
    violation_id: readParam(search.violation_id),
    submission_id: readParam(search.submission_id),
    letter_kind: readParam(search.letter_kind),
  }),
});

/**
 * The one destination behind every letter door. A contextual door arrives with
 * its subject on the URL and the officer starts at "start from" — a door for
 * one letter also names its starter (`letter_kind`), which Start-from picks
 * when the subject can fill it; the + menu and the Notices CTA arrive blank and
 * the subject is picked first — the same page either way, so there is one
 * compose flow to keep correct rather than six.
 *
 * The draft is created, not sent: `createNotice` freezes the merge values, the
 * branding and the addressee, then the letter opens in its editor.
 */
function NewNoticePage() {
  const { t } = useTranslation(["notices", "common"]);
  const navigate = useNavigate();
  const search = useSearch({ from: Route.id });
  const { can, isLoading: permissionsLoading } = usePermissions();
  const create = useCreateNotice();
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  // Read on mount so the account-codes switch is in the cache before a refusal
  // whose sentence depends on it arrives.
  useOrgComplianceSettings(orgId);

  const [subject, setSubject] = useState<NoticeSubjectRef | null>(() => subjectFromSearch(search));
  const [source, setSource] = useState<LetterSource | null>(null);
  const [templateTitle, setTemplateTitle] = useState("");
  const [subjectLine, setSubjectLine] = useState("");
  const [subjectTouched, setSubjectTouched] = useState(false);
  const [locale, setLocale] = useState<NoticeLocale>("en");
  const [override, setOverride] = useState<NoticeRecipient | null>(null);
  const [ccRecipients, setCcRecipients] = useState<NoticeCcRecipient[]>([]);
  const [enclosureIds, setEnclosureIds] = useState<string[]>([]);
  const [failure, setFailure] = useState<NoticeComposeFailure | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [warning, setWarning] = useState(false);

  const summary = useSubjectSummary(subject);
  // The premise the door arrived at: the door's own premise, or the one its
  // assembly sits at, learned once that subject's read answers. A case and an
  // inspection each belong to the premise they sit at, so a subject re-picked
  // at another premise leaves the letter with neither to print; an unknown
  // premise is no reason to drop them. The ids still ride the create body, so
  // a letter composed elsewhere is the server's 422 to name.
  const doorKey = subjectKey(subjectFromSearch(search));
  const [doorPremise, setDoorPremise] = useState<string | null>(search.facility_id ?? null);
  if (doorPremise === null && summary.facilityId !== null && subjectKey(subject) === doorKey) {
    setDoorPremise(summary.facilityId);
  }
  const atDoorPremise = !(
    doorPremise !== null &&
    summary.facilityId !== null &&
    summary.facilityId !== doorPremise
  );
  const caseBound = search.violation_id !== undefined && atDoorPremise;
  // A result family prints the bound submission's own facts, so the page reads
  // it back: only an accepted premise inspection or survey is an inspection it
  // can print, and only an accepted device test of the subject assembly a test.
  // The read is sent only when the door bound a submission.
  const boundSubmission = useFormSubmission(search.submission_id);
  const inspectionBound = atDoorPremise ? boundInspectionFact(boundSubmission) : false;
  const testBound = boundTestFact(subject, boundSubmission);
  // The same cached read the recipient card renders — generation seeds the
  // subject premise's flagged contacts onto the letter, and the composer
  // pre-checks exactly that list so a hand-written letter copies whom an
  // automated one would.
  const resolve = useResolveNoticeRecipient(subject);
  const ccSeededFor = useRef<string | null>(null);
  // The same cached read step 4 renders; its defaults are ticked here so the
  // ticks and the copies seed and release under one rule.
  const candidateItems = useNoticeEnclosureCandidates(subject).data?.items;
  const enclosuresSeededFor = useRef<string | null>(null);

  // A New installation waiting for review at the facility may be the very
  // assembly this letter says is missing: a letter from an open case it could
  // resolve, or the pending-disconnection family, warns before it generates.
  // Create draft holds until both reads answer, so a fast click never skips it.
  const canReadChangeRequests = can("change_requests", "read");
  const caseRead = useViolation(canReadChangeRequests ? search.violation_id : null);
  const caseFile = caseRead.data;
  const pending = usePendingNewInstallation(
    caseFile?.facility_id ?? summary.facilityId,
    (caseFile !== undefined && caseAwaitsInstallation(caseFile)) ||
      (source?.kind === "template" && source.letterKind === PENDING_DISCONNECTION_KIND),
  );
  const pendingInstallation = pending.row;
  const checkingPending = caseRead.isLoading || pending.loading;
  // A warning answered by the letter no longer calling for it never returns unasked.
  if (warning && !pendingInstallation) setWarning(false);

  // Stable identity: Start-from drops a pick that stops being legal from an
  // effect, and an inline callback would re-arm that effect on every render.
  const handleSource = useCallback((next: LetterSource | null, title: string) => {
    setSource(next);
    setTemplateTitle(title);
  }, []);

  // Pre-check the seed ONCE per subject. A refetch must not resurrect a copy
  // the officer removed, and a copy typed while the resolve was still in
  // flight is kept rather than swept away — both are officer decisions, and
  // the submitted list is client-authoritative.
  const seed = resolve.data?.cc_seed;
  useEffect(() => {
    const key = subjectKey(subject);
    if (key === null || seed === undefined || ccSeededFor.current === key) return;
    ccSeededFor.current = key;
    if (seed.length === 0) return;
    setCcRecipients((prev) => {
      const held = new Set(
        prev
          .map((copy) => copy.facility_contact_id)
          .filter((id): id is string => typeof id === "string"),
      );
      return [
        ...prev,
        ...seed.filter((copy) => {
          const id = copy.facility_contact_id;
          return typeof id !== "string" || !held.has(id);
        }),
      ];
    });
  }, [subject, seed]);

  // Tick the default enclosures ONCE per subject, when its candidates first
  // answer — each assembly's latest test, held to the dispatch cap. A refetch
  // never re-ticks what the officer unticked. The letter is not rendered yet,
  // so the cap counts the enclosures alone.
  useEffect(() => {
    const key = subjectKey(subject);
    if (key === null || candidateItems === undefined || enclosuresSeededFor.current === key) return;
    enclosuresSeededFor.current = key;
    const defaults = defaultEnclosureTicks(candidateItems, null);
    if (defaults.length > 0) setEnclosureIds(defaults);
  }, [subject, candidateItems]);

  // The subject line the officer would have typed anyway. It keeps tracking the
  // template and the subject until the officer edits it, at which point it is
  // theirs and nothing overwrites it.
  useEffect(() => {
    if (subjectTouched) return;
    setSubjectLine(
      [templateTitle, summary.label].filter((part) => part && part.trim() !== "").join(" — "),
    );
  }, [templateTitle, summary.label, subjectTouched]);

  // `usePermissions()` reads deny-all until its envelope resolves; refusing
  // earlier would tell a caller who can compose that they cannot.
  if (permissionsLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }
  if (!can("notices", "create")) {
    return (
      <div className="p-8">
        <EmptyState
          title={t("compose.forbidden.title")}
          description={t("compose.forbidden.description")}
          actionLabel={t("common:back")}
          onAction={() => void navigate({ to: "/notices" })}
        />
      </div>
    );
  }

  // A new subject resolves a different addressee, a different contact list and
  // different report forms, so neither the override, the copies nor the
  // enclosures may outlive the subject that justified them — including a
  // re-pick of the same record, which is why both seeding latches are
  // released here rather than keyed on identity alone.
  const changeSubject = (next: NoticeSubjectRef | null) => {
    setOverride(null);
    setCcRecipients([]);
    setEnclosureIds([]);
    ccSeededFor.current = null;
    enclosuresSeededFor.current = null;
    setSubject(next);
  };

  const overrideNameMissing = override !== null && override.name.trim() === "";
  const canSubmit =
    subject !== null && source !== null && !overrideNameMissing && !create.isPending;

  const handleCreate = async () => {
    if (!subject || !source) return;
    setFailure(null);
    setErrorMessage(undefined);
    const body: NoticeCreate = {
      ...noticeSubjectParams(subject),
      // `free_form` is the POSITIVE declaration of a blank letter, and it is
      // always sent: absent it the server requires a template and answers 422
      // `template_or_body`, so a blank letter cannot be asked for by omission.
      ...(source.kind === "template"
        ? { template_id: source.templateId, free_form: false }
        : { free_form: true }),
      ...(subjectLine.trim() !== "" ? { subject: subjectLine.trim() } : {}),
      locale,
      ...(override ? { recipient: override } : {}),
      ...(ccRecipients.length > 0 ? { cc_recipients: ccRecipients } : {}),
      ...(enclosureIds.length > 0 ? { enclosure_output_ids: enclosureIds } : {}),
      ...(search.violation_id ? { violation_id: search.violation_id } : {}),
      ...(search.submission_id ? { form_submission_id: search.submission_id } : {}),
      // The composer drafts the letter for the officer to edit: it never asks
      // the server to approve and send it in the same request.
      send_now: false,
    };
    try {
      const created = await create.mutateAsync(body);
      await navigate({ to: "/notices/$id/edit", params: { id: created.id } });
    } catch (err) {
      const composeFailure = noticeComposeFailure(err);
      if (composeFailure) {
        setFailure(composeFailure);
        // An output regenerated since the list was read is gone from it once
        // re-read, and the next tick drops it from the set.
        if (composeFailure.code === "enclosure_not_eligible") {
          void queryClient.invalidateQueries({
            queryKey: noticeKeys.enclosureCandidates(orgId ?? "", subject),
          });
        }
        return;
      }
      setErrorMessage(
        err instanceof Error ? mapApiError(err.message) : t("compose.errors.generic"),
      );
    }
  };

  return (
    <div className="p-8">
      <PageHeader title={t("compose.title")} subtitle={t("compose.subtitle")} />
      <div className="mt-6 max-w-2xl space-y-6">
        <AboutCard
          subject={subject}
          summary={summary}
          onPick={changeSubject}
          onClear={() => changeSubject(null)}
        />

        <StartFromCard
          subject={{
            kind: subject?.kind ?? null,
            hasTestDueDate: summary.hasTestDueDate,
            caseBound,
            inspectionBound,
            testBound,
          }}
          source={source}
          onSource={handleSource}
          subjectLine={subjectLine}
          onSubjectLine={(value) => {
            setSubjectTouched(true);
            setSubjectLine(value);
          }}
          locale={locale}
          onLocale={setLocale}
          preferredKind={search.letter_kind}
        />

        <RecipientResolveCard
          subject={subject}
          facilityId={summary.facilityId}
          account={subject ? summaryAccount(subject.kind, summary) : null}
          printOnly={
            source === null ? undefined : source.kind === "template" ? source.printOnly : false
          }
          override={override}
          onOverride={setOverride}
        />

        <EnclosuresCard subject={subject} value={enclosureIds} onChange={setEnclosureIds} />

        <CcCard facilityId={summary.facilityId} value={ccRecipients} onChange={setCcRecipients} />

        {failure && <ComposeFailureAlert failure={failure} subjectKind={subject?.kind ?? null} />}

        {errorMessage && (
          <p role="alert" className="text-sm text-danger">
            {errorMessage}
          </p>
        )}

        {warning && pendingInstallation && (
          <PendingInstallationWarning
            row={pendingInstallation}
            onReview={() =>
              void navigate({
                to: "/test-activity/change-requests/$setId",
                params: { setId: pendingInstallation.id },
              })
            }
            onGenerate={() => {
              setWarning(false);
              void handleCreate();
            }}
          />
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => void navigate({ to: "/notices" })}>
            {t("common:cancel")}
          </Button>
          <Button
            onClick={() => {
              if (pendingInstallation) setWarning(true);
              else void handleCreate();
            }}
            disabled={!canSubmit}
            loading={create.isPending || checkingPending}
          >
            {t("compose.actions.create")}
          </Button>
        </div>
      </div>
    </div>
  );
}
